// Per-state content for enei.in — hand-written, voice-tight.
// Each state gets: hero, sensory intro, when-to-go, 5 places, food, practical.

export const STATE_PAGES = {
    assam: {
        slug: "assam",
        name: "Assam",
        tagline: "Rivers, rhinos, tea.",
        capital: "Dispur (Guwahati)",
        heroImage:
            "https://images.unsplash.com/photo-1602020277972-99978250c8bd?auto=format&fit=crop&w=1920&q=70",
        intro:
            "The Brahmaputra is wider than you think — and louder. It is the verb of this state. The land takes its shape from the river: tea gardens to the north, paddy in the middle, and Majuli, a 16th-century Vaishnavite world floating at its centre, slowly being eaten by the same river that made it. To travel in Assam is to travel sideways. Most things happen by ferry, by lunch invitation, or by chance.",
        seasons: [
            { months: "Nov – Mar", note: "Cool, dry, perfect. Kaziranga safaris are in full swing — best Indian rhino sightings on earth." },
            { months: "Apr (Bihu)", note: "Three days of dance, drum, and yellow mustard. Locals will pull you in. Let them." },
            { months: "Jun – Sep", note: "Monsoon. The Brahmaputra rises. Kaziranga closes. Tea gardens look as green as they ever will." },
        ],
        places: [
            {
                name: "Majuli",
                line: "The world's largest river island. Mask-makers, satras, mustard fields. Stay in a bamboo cottage at Garmur and wake to the river.",
            },
            {
                name: "Kaziranga",
                line: "Two-thirds of all wild Indian rhinos live here. Dawn safari in the central range. Diphlu River Lodge if you wish to sleep well after.",
            },
            {
                name: "Sivasagar",
                line: "The old Ahom capital. A series of monumental tanks and a maidam-strewn plain. Quiet most of the year. Worth a slow afternoon.",
            },
            {
                name: "Dibrugarh tea bungalows",
                line: "A handful of 1875-era planters' bungalows still serve breakfast on a wicker tray and let you walk the gardens before sunrise.",
            },
            {
                name: "Hajo",
                line: "Rare in India: Hindu, Muslim and Buddhist pilgrim sites within 2 km of one another. A short, calming detour from Guwahati.",
            },
        ],
        food: [
            "Khar — alkaline curry made from sun-dried banana skin. The first dish at any Assamese meal.",
            "Masor tenga — a tangy fish curry with tomato, lemon, or thekera; perfect on a hot afternoon.",
            "Pitha — rice-flour cakes around Bihu. Til-pitha (sesame) is the gateway.",
            "Second-flush Assam tea, brewed by hand at any decent tea bungalow.",
        ],
        getting_there:
            "Fly into Guwahati (LGB International). Dibrugarh has its own airport for upper Assam. The Brahmaputra is best crossed at Bogibeel — the longest road-rail bridge in India.",
        permits:
            "No special permits for Indian or foreign travellers. Foreign visitors should keep passport copies handy at Kaziranga gates.",
    },

    meghalaya: {
        slug: "meghalaya",
        name: "Meghalaya",
        tagline: "Where the clouds live.",
        capital: "Shillong",
        heroImage:
            "https://images.unsplash.com/photo-1669628182335-4c50980f419d?auto=format&fit=crop&w=1920&q=70",
        intro:
            "Meghalaya means 'abode of clouds', and once you've spent a Saturday in Cherrapunji you understand it less as poetry and more as forecast. This is matrilineal Khasi country — the women own the land, the youngest daughter inherits the house. The forests grow living root bridges that take 200 years to mature. Walk into Mawlynnong on a Sunday and the village is silent: they are all at church.",
        seasons: [
            { months: "Sept – Nov", note: "The wettest months are over but the waterfalls are still in full voice. Air is sharp and clean." },
            { months: "Dec – Feb", note: "Cool, foggy mornings. Cherry blossoms in mid-November. Hostels and homestays at their gentlest." },
            { months: "Jun – Aug", note: "Wettest place on earth, properly. Travel slows. Some trails close. The rain is part of the experience." },
        ],
        places: [
            {
                name: "Nongriat",
                line: "3,500 stone steps down to the famous double-decker root bridge. Stay two nights at the bottom — it changes the place.",
            },
            {
                name: "Mawlynnong",
                line: "Cleanest village in Asia, and they are quietly proud of it. Sunday mornings are a kind of meditation.",
            },
            {
                name: "Dawki",
                line: "The Umngot river is so clear that boats look like they're floating in air. Best visited Nov – Mar; muddy in monsoon.",
            },
            {
                name: "Mawphlang sacred grove",
                line: "Khasi sacred forest. Take nothing — not a leaf — out. Walk slowly. The light alone is worth the trip.",
            },
            {
                name: "Shillong",
                line: "Northeast's cultural capital. Bands play live every weekend at Café Shillong. Police Bazaar is breakfast at 8 a.m.",
            },
        ],
        food: [
            "Jadoh — pork-blood rice. Earthy, deep. Eat it where the locals do, in a small Khasi-only kitchen.",
            "Doh-khlieh — pork salad with onion, ginger and chilli. Fresh, sharp, ideal on a hot afternoon.",
            "Tungrymbai — fermented soybean stew with pork. Strong on the nose, soft on the palate.",
            "Pukhlein — sweet rice-flour fritter, fried in mustard oil. Tea-time staple in any Khasi home.",
        ],
        getting_there:
            "Fly to Guwahati. Then a 3-hour shared-sumo or private taxi up to Shillong. From Shillong, it's another 2 hours to Cherrapunji, 3 to Dawki, 4 to Mawlynnong.",
        permits:
            "No permits needed for Indian or foreign travellers. Some sacred groves and root-bridge trails require a small village fee.",
    },

    nagaland: {
        slug: "nagaland",
        name: "Nagaland",
        tagline: "Sixteen tribes. One blue sky.",
        capital: "Kohima",
        heroImage:
            "https://images.unsplash.com/photo-1692652567309-5ac9ae7fe6f7?auto=format&fit=crop&w=1920&q=70",
        intro:
            "Nagaland is the only state in India where the sky feels a size larger than the land. Sixteen tribes, sixteen languages, sixteen ways of building a longhouse. Hospitality here is a discipline, not a gesture: you'll be fed before you're asked your name. Visit Khonoma, Asia's first green village, and you'll learn that sustainability isn't a buzzword — it's a system that has been running since 1100 AD.",
        seasons: [
            { months: "Dec 1–10", note: "Hornbill Festival at Kisama. Sixteen tribes, one fire. Book accommodation in October — earlier in pandemic years." },
            { months: "Oct – Mar", note: "Cool and clear. The hills are at their most photogenic. Most homestays are open." },
            { months: "Jun – Aug", note: "Dzukou Valley blooms. The world's most underrated trek if you can take the rain." },
        ],
        places: [
            {
                name: "Kohima",
                line: "World War II cemetery — a quiet, well-kept hill of marble. The only place in India where the British 14th Army is properly remembered.",
            },
            {
                name: "Kisama Heritage Village",
                line: "Permanent host of Hornbill Festival. Even off-season, the morungs are open and the elders willing to talk.",
            },
            {
                name: "Khonoma",
                line: "Asia's first green village. Angami homestays, terraced fields, no plastic. You will leave changed.",
            },
            {
                name: "Dzukou Valley",
                line: "Trek 14 km from Viswema. Lavender-bowl valley, stone huts, the kind of silence cities forget.",
            },
            {
                name: "Mon (Konyak country)",
                line: "Headhunter tattoos still visible on a few remaining elders. A long, thoughtful drive — but unforgettable.",
            },
        ],
        food: [
            "Smoked pork with bamboo shoot — the unofficial state dish. Best eaten in someone's home.",
            "Anishi — yam-leaf paste fermented for a year. Earthy. Acquired. Deeply Naga.",
            "Axone — fermented soybean. Strong, beautiful with rice and a pork stew.",
            "Zutho — Angami rice beer, served warm in a bamboo cup. One sip is hospitality.",
        ],
        getting_there:
            "Fly to Dimapur (Northeast's only Nagaland airport), then 3 hours by road up to Kohima. Mon takes a long, scenic 9-hour drive from Kohima — break the journey at Mokokchung.",
        permits:
            "Indian travellers need no permit. Foreign nationals require a Protected Area Permit (PAP) — apply online via your tour operator at least 30 days in advance.",
    },

    arunachal: {
        slug: "arunachal",
        name: "Arunachal Pradesh",
        tagline: "Where dawn is a discipline.",
        capital: "Itanagar",
        heroImage:
            "https://images.pexels.com/photos/2815093/pexels-photo-2815093.jpeg?auto=compress&cs=tinysrgb&w=1920&q=70",
        intro:
            "Arunachal is the first state in India to see the sun. It is also the most linguistically rich on earth per capita — over 100 languages across 26 tribes. From Bomdila the road climbs through Sela Pass and drops into Tawang, the second-largest monastery in the world. Further east, towards Mechuka and Anini, you arrive at places where Indian phone networks stop and the mountains do their work uninterrupted.",
        seasons: [
            { months: "Apr – Oct", note: "The only time most high passes are open. Mechuka, Tawang, Ziro — all best now." },
            { months: "Late Sept", note: "Ziro Music Festival in the Apatani rice paddies. Camp on-site." },
            { months: "Nov – Mar", note: "Tawang gets snow; the monastery is at its quietest. Sela may close briefly." },
        ],
        places: [
            {
                name: "Tawang",
                line: "Second-largest monastery on earth. Walk to the 5 a.m. prayer in your own silence. Stay three days, not one.",
            },
            {
                name: "Sela Pass",
                line: "13,700 ft. A frozen lake. A stone arch. A flask of butter tea waiting at the army post.",
            },
            {
                name: "Ziro Valley",
                line: "Apatani country. Rice paddies that are also fish farms. Late September brings the music festival.",
            },
            {
                name: "Mechuka",
                line: "Memba valley closer to Lhasa than Delhi in feel. The road in is the journey. Royal Enfield recommended.",
            },
            {
                name: "Nameri / Pakke",
                line: "Birdwatcher's heaven. Hornbills overhead. River-rafting on the Jia Bharali in winter.",
            },
        ],
        food: [
            "Thukpa — Tibetan-influenced noodle soup. Standard in any Tawang kitchen.",
            "Apong — sticky-rice beer, fermented in bamboo. Different in every village.",
            "Pika pila — fermented bamboo-shoot pickle. Staple in Apatani homes.",
            "Yak cheese, in Tawang. Strong, ancient, slightly briny.",
        ],
        getting_there:
            "Fly to Guwahati, drive 5 hours to Bomdila, another 5 to Tawang. For Mechuka: fly to Dibrugarh → Pasighat → Aalo → Mechuka (3 driving days). Helicopter service operates between Guwahati and Tawang during peak season.",
        permits:
            "All travellers (including Indians) need an Inner Line Permit (ILP). Apply online at arunachalilp.com or via your tour operator. Foreigners require a Protected Area Permit (PAP) — only issued in groups of 2+ via a registered operator.",
    },

    manipur: {
        slug: "manipur",
        name: "Manipur",
        tagline: "A lake that breathes.",
        capital: "Imphal",
        heroImage:
            "https://images.unsplash.com/photo-1674722612663-c34ad2c24648?auto=format&fit=crop&w=1920&q=70",
        intro:
            "Manipur is the only state in the world where a national park floats. The Keibul Lamjao is built on phumdi — drifting mats of vegetation on Loktak Lake — home to the sangai, a deer that walks on water. The Meitei here keep a 500-year-old all-women's market in the heart of Imphal. Manipuri Raas Leela is danced barefoot, in temples, with a discipline that takes a lifetime to learn.",
        seasons: [
            { months: "Oct – Mar", note: "Cool, clear, ideal. Loktak is at its most photogenic in early winter." },
            { months: "Late Nov", note: "Sangai Festival. Music, polo, classical dance, all over Imphal." },
            { months: "Apr – Sep", note: "Warm and wet. Loktak boats run; the lake is fuller, the phumdis greener." },
        ],
        places: [
            {
                name: "Loktak Lake",
                line: "The world's only floating lake. Stay in a phumdi homestay; wake to mist, fish, the occasional sangai sighting.",
            },
            {
                name: "Ima Keithel",
                line: "An all-women market that has run for 500 years. 4,000 stalls. No middlemen. Older than half the world's nation-states.",
            },
            {
                name: "Kangla Fort",
                line: "Sacred capital of the Meitei kings. The kangla-sha (dragon-lions) at the gate are the soul of the city.",
            },
            {
                name: "Keibul Lamjao",
                line: "World's only floating national park. Home to the dancing deer. Slow boats. Patient eyes.",
            },
            {
                name: "Andro & Sekta",
                line: "Pottery villages and small archaeological digs. A quiet rural day out from Imphal.",
            },
        ],
        food: [
            "Eromba — boiled vegetables crushed with fermented fish. Spicy, deeply local.",
            "Singju — raw vegetable salad with fermented fish and chilli. The Manipuri household equivalent of chaat.",
            "Chamthong — light vegetable broth. Comforting after a wet day.",
            "Iromba black rice (chak-hao) — slow-cooked into a deep, plum-coloured kheer.",
        ],
        getting_there:
            "Fly to Imphal (IMF). Loktak is a 1.5-hour drive south. Roads can be temperamental — keep buffer days during monsoon.",
        permits:
            "No permit for Indian travellers. Foreign nationals require ILP equivalent — check with the Home Department before travel.",
    },

    mizoram: {
        slug: "mizoram",
        name: "Mizoram",
        tagline: "Quiet hills, loud kindness.",
        capital: "Aizawl",
        heroImage:
            "https://images.unsplash.com/photo-1742489468038-b7e948b6fab1?auto=format&fit=crop&w=1920&q=70",
        intro:
            "Mizoram is the quietest state in India. Aizawl is built on a single ridge, its houses clinging to the slope like a held breath. On Sundays the entire state goes still for church — shops shuttered, taxis parked. By Monday, it opens again with tea and chow. Bamboo is currency, scaffolding, and lunch. The Mizo word 'tlawmngaihna' has no English equivalent — it means a kindness performed without expectation. You will receive it constantly.",
        seasons: [
            { months: "Oct – Mar", note: "Cool, dry, lovely. Champhai grapes ripen, Reiek hills are clear." },
            { months: "Mar (Chapchar Kut)", note: "Mizo spring festival — bamboo dance, gospel, woodsmoke." },
            { months: "Jun – Sep", note: "Monsoon hits hard. Some roads close. Aizawl quietens further." },
        ],
        places: [
            {
                name: "Aizawl",
                line: "A capital you can walk in a day. Best café: the cake shop near Bara Bazar. Best view: any rooftop at sunset.",
            },
            {
                name: "Reiek",
                line: "1,500 m hill, 30 km from Aizawl. The reconstructed traditional Mizo village explains the bamboo architecture.",
            },
            {
                name: "Champhai",
                line: "Border country toward Myanmar. Vineyards, churches, a slower variety of silence.",
            },
            {
                name: "Hmuifang",
                line: "Quiet ridge, big sky. A weekend cabin away from Aizawl.",
            },
            {
                name: "Vantawng Falls",
                line: "Highest waterfall in Mizoram. Best in monsoon, when it earns its name.",
            },
        ],
        food: [
            "Bai — boiled vegetable, pork, fermented bamboo shoot. The Mizo soul food.",
            "Vawksa rep — smoked pork stew. Cooks for hours.",
            "Misa Mach Poora — small grilled river prawns. Eaten with rice, hand only.",
            "Sawhchiar — savoury rice porridge with meat. Sunday after-church food.",
        ],
        getting_there:
            "Fly to Lengpui (Aizawl), or via Silchar in Assam (5-hour drive). The Lengpui runway is short and dramatic — landings get cancelled in heavy weather.",
        permits:
            "Indian travellers need an ILP (online via Mizoram Tourism). Foreigners require ILP + register at the Aizawl SP office on arrival.",
    },

    tripura: {
        slug: "tripura",
        name: "Tripura",
        tagline: "Bengal's quiet sibling.",
        capital: "Agartala",
        heroImage:
            "https://images.unsplash.com/photo-1762707675084-01ac3bd4e9f0?auto=format&fit=crop&w=1920&q=70",
        intro:
            "Tripura is small enough to drive across in a day, and old enough to make you slow down anyway. The Manikya kings ruled here for 500 years — Ujjayanta Palace at dusk turns the colour of marigolds. Out at Unakoti, 9th-century rock carvings stare back at you, half-swallowed by jungle. Tripura is also where the Bengali tongue meets Kokborok. Bring questions. Locals will offer answers, and second helpings.",
        seasons: [
            { months: "Oct – Mar", note: "Cool and dry. The state is at its most photogenic; Garia Puja kicks off mid-April." },
            { months: "Apr (Garia)", note: "Tripuri tribal harvest festival. Bamboo poles, marigold garlands, three days of music." },
            { months: "Jun – Sep", note: "Monsoon. Rich green, occasional flooding in lowlands. Unakoti looks otherworldly in the rain." },
        ],
        places: [
            {
                name: "Ujjayanta Palace",
                line: "1901 royal palace, now a museum. The lawns at sunset are still public space, still beautifully kept.",
            },
            {
                name: "Unakoti",
                line: "9th-century rock-cut Hindu sculptures, scattered through jungle. India's quiet, lesser-known Angkor.",
            },
            {
                name: "Neermahal",
                line: "Water palace on Rudrasagar Lake. Take the boat at sunrise. No crowds.",
            },
            {
                name: "Tripura Sundari Temple",
                line: "One of the 51 shaktipeethas. The site is calmer than its reputation suggests.",
            },
            {
                name: "Jampui Hills",
                line: "Far north, near Mizoram border. Tripura's coffee belt and orange groves — and it's at altitude, so cool year-round.",
            },
        ],
        food: [
            "Mui borok — Tripuri cuisine, hardly any oil; smoked, steamed, fermented.",
            "Bangwi — sticky rice cooked inside bamboo over wood fire.",
            "Chakhwi — bamboo-shoot stew with vegetables; Tripuri household staple.",
            "Wahan mosdeng — minced pork with chillies and herbs.",
        ],
        getting_there:
            "Fly to Agartala (IXA). The state is well-connected by road; many places are reachable in half-day trips from the capital.",
        permits:
            "No permits required. Domestic travel is straightforward. Foreigners may register with FRRO if staying long-term.",
    },

    sikkim: {
        slug: "sikkim",
        name: "Sikkim",
        tagline: "Prayer flags. Cold air. Clarity.",
        capital: "Gangtok",
        heroImage:
            "https://images.unsplash.com/photo-1672096099708-e9e379cd63c7?auto=format&fit=crop&w=1920&q=70",
        intro:
            "Sikkim is India's first fully organic state — the radish in your thukpa was pulled from the ground at sunrise. On a clear morning at Tiger Hill, Kanchenjunga turns gold for ninety seconds, then is gone. The kingdom merged with India only in 1975; the king's old palace is now a hotel. Buddhism here is Tibetan, gentle, omnipresent. The prayer flags are not decoration. They are wishes left in the wind.",
        seasons: [
            { months: "Mar – May", note: "Rhododendrons in West Sikkim. Cherry blossom in mid-April. Best for trekking." },
            { months: "Oct – Nov", note: "Cool, clear, Kanchenjunga visible most mornings. The great photographers' season." },
            { months: "Feb (Losar)", note: "Tibetan New Year. Butter tea, butter lamps, Cham dances at Rumtek and Pemayangtse." },
        ],
        places: [
            {
                name: "Pelling",
                line: "West Sikkim. Wake to Kanchenjunga from the bedroom window. Visit Pemayangtse and Khecheopalri.",
            },
            {
                name: "Yuksom",
                line: "First capital of Sikkim. Trailhead for Goecha La. Older silence than Pelling.",
            },
            {
                name: "Lachung & Yumthang",
                line: "Far north. The Valley of Flowers in summer; snow desert in winter. Permits needed beyond Mangan.",
            },
            {
                name: "Rumtek Monastery",
                line: "Seat of the Karmapa. Largest monastery in Sikkim. Quiet evenings; arrive late afternoon.",
            },
            {
                name: "Tsomgo & Nathu La",
                line: "High-altitude lake at 12,400 ft. Clear days only. Indian travellers can visit Nathu La with a permit on Wed/Thu/Sat/Sun.",
            },
        ],
        food: [
            "Phagshapa — pork strips with radish and chilli. The flavour of West Sikkim.",
            "Sel roti — ring-shaped rice doughnut, crisp outside. Tea-time staple.",
            "Gundruk soup — fermented leafy greens. Sour, warming, native.",
            "Chhurpi — yak cheese. Hard variety lasts hours of chewing.",
        ],
        getting_there:
            "Fly to Bagdogra (IXB) or Pakyong (Sikkim's own — weather-dependent). 4 hours by road to Gangtok, 5 to Pelling. The new Sevoke–Rangpo rail line will cut journey time once operational.",
        permits:
            "Indians: no permit for most areas; ILP for Nathu La, North Sikkim, and high-altitude lakes (issued same-day at Gangtok). Foreigners: ILP for entry; Protected Area Permit for Tsomgo, Nathu La, North Sikkim — only via registered operators.",
    },
};
